import React from 'react';
import { BannerAlert, BannerAlertVariant } from '@amzn/storm-ui';
import { BANNER_LEVEL } from 'src/app/slice';
import { StyledAlert } from '../styles';

interface BannerShellProps {
  bannerLevel: BANNER_LEVEL;
  /** StormUI variant, e.g. the lowercased banner type ('error' | 'warning' | 'info'). */
  variant: BannerAlertVariant;
  withCloseButton: boolean;
  onClose: () => void;
  /** Page-level only: the Alert header. Ignored at console level (BannerAlert has no header). */
  header?: string;
  /** Forwarded as data-test-id so existing tests / e2e keep matching. */
  dataTestId: string;
  children: React.ReactNode;
}

/**
 * The one place the console-vs-page banner primitive is chosen.
 *
 * Console-level banners render StormUI's full-bleed `BannerAlert`; page-level
 * banners render `StyledAlert` (an `Alert`) with a `header`. Before this, that
 * fork was copy-pasted into SingleBanner, SingleBannerWithModal and
 * ConsolidatedBanner - and SingleBannerWithModal only ever had the console
 * branch. Each renderer now passes its content as `children` and this owns the
 * wrapper, so the two levels cannot drift apart.
 */
export const BannerShell: React.VFC<BannerShellProps> = ({
  bannerLevel,
  variant,
  withCloseButton,
  onClose,
  header,
  dataTestId,
  children,
}) => {
  if (bannerLevel === BANNER_LEVEL.CONSOLE) {
    return (
      <BannerAlert
        data-test-id={dataTestId}
        variant={variant}
        withCloseButton={withCloseButton}
        onClose={onClose}
      >
        {children}
      </BannerAlert>
    );
  }
  return (
    <StyledAlert
      data-test-id={dataTestId}
      variant={variant}
      withCloseButton={withCloseButton}
      onClose={onClose}
      header={header}
    >
      {children}
    </StyledAlert>
  );
};
